'use client';

import LLInput from '@next-app/components/elements/LLInput/LLInput';
import LLHeading from '@next-app/components/elements/LLHeading/LLHeading';
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import LLPopover from '@next-app/components/elements/LLPopover/LLPopover';
import {
  formatPhoneNumber,
  getUnformattedNumber,
} from '@next-app/utils/form-validation';
import { useDispatch, useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import { LoqateApiAddress } from '@next-app/interface/AddressAutoFill';
import {
  containsEmptyValues,
  containsSomeEmptyValues,
  isNotEmptyObject,
  isNotNullOrUndefinedorEmpty,
} from '@next-app/utils/common-utils';
import {
  setBillingAddressError,
  setBillingRequest,
  setCreditCardFormError,
  setIsSubmitOrder,
  setLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  BillingAddressFormFields,
  BillingAddressFormInitialState,
} from './BillingAddressFormFields';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_ACCEPTED_CHARACTERS,
  FIELD_VALIDATION_RULES,
  PAYMENT,
} from '@next-app/constants/CheckoutConstants';
import {
  AddressInfo,
  FormException,
} from '@next-app/lib/features/checkout/Checkout.interface';
import AddressAutoFill from '@next-app/components/shared/AddressAutoFill/AddressAutoFill';
import CountryList from '../DeliveryAddressForm/CountryList';
import StateList from '../DeliveryAddressForm/StateList';
import {
  selectBillingAddress,
  selectBillingAddressFormErrors,
  selectCreditCardFormErrors,
  selectPaymentMethodType,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import LLSwitch from '@next-app/components/elements/LLSwitch/LLSwitch';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import {
  selectDynSess,
  selectIsFurniture,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import {
  applyBilling,
  handleCheckoutServerErrors,
  sendOnBlurDTMEvent,
  triggerCheckoutFormErrorEvent,
} from '@next-app/utils/CheckoutHelper';
import { CreditCardFormFields } from '../CCCardDetailsForm/CreditCardFormFields';
import Loader from '@next-app/components/shared/Loader/Loader';

interface IBillingAddressFormProps {
  id: string;
  formHeading?: string;
  onCancel: Function;
  isNewAddress: boolean;
  isPOACard?: boolean;
  setIsExpandable?: Function;
  setShowPaymentCollapse?: Function;
  isBillingAdd?: boolean;
  makeDefaultPayment?: boolean;
  ccSelectedId?: string;
  selectedBillingAddress?: any;
  displaySaveCancelButtons?: boolean;
}

const BillingAddressForm: FC<IBillingAddressFormProps> = ({
  formHeading,
  id,
  onCancel,
  isPOACard = true, // Default value set here
  setIsExpandable,
  setShowPaymentCollapse,
  isBillingAdd = true,
  makeDefaultPayment = false,
  ccSelectedId = PAYMENT.ADD_NEW_CC,
  isNewAddress,
  selectedBillingAddress,
  displaySaveCancelButtons,
}) => {
  // dispatch
  const dispatch = useDispatch();

  // selectors
  const billingAddressData = useSelector(selectBillingAddress);
  const billingAddressFormErrors = useSelector(selectBillingAddressFormErrors);
  const selectedPaymentMethodType = useSelector(selectPaymentMethodType);
  const isFurniture = useSelector(selectIsFurniture);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);

  const dynSessConf = useSelector(selectDynSess);
  const poaForm = billingAddressData?.poaFormData;
  const creditCardForm = billingAddressData?.creditCardFormData;
  const {
    poNumber = '',
    taxExempt = false,
    paymentMethod = '',
    ownerId = '',
    repositoryId = '',
  } = poaForm || {};

  const {
    cardHolderName,
    cardVerificationNumber,
    creditCardNumber,
    expirationMonth,
    expirationYear,
    paymentMethod: ccPaymentMethod,
    ownerId: ccOwnerId,
    repositoryId: ccRepositoryId,
  } = creditCardForm;

  const {
    firstName,
    lastName,
    address1,
    address2,
    companyName,
    city,
    country,
    phoneNumber,
    postalCode,
    state,
    attention,
    active,
    avsStatus,
  } = billingAddressData.billingAddressFormData;

  // state
  const [initialBillingAdd, setInitialVillingAdd] = useState(
    billingAddressData.billingAddressFormData,
  );
  const [formData, setFormData] = useState<Record<string, any>>(
    selectedBillingAddress || BillingAddressFormInitialState,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>(
    billingAddressFormErrors || {},
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loqateAddressType, setLoqateAddressType] = useState<string>('');

  const formRef = useRef<HTMLFormElement>(null);

  const scrollToFirstError = () => {
    if (formRef.current) {
      const firstErrorElement = formRef.current.querySelector('.error-msg');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  const scrollToErrorField = () => {
    if (formErrors) {
      const firstErrorField = Object.keys(formErrors).find(
        (key) => formErrors[key],
      );
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  useEffect(() => {
    if (billingAddressFormErrors) {
      setFormErrors((prev) => ({
        ...prev,
        ...billingAddressFormErrors,
      }));
    }
  }, [billingAddressFormErrors]);

  const updateFormData = (newState: Record<string, any>) => {
    if (newState?.state)
      setFormErrors((prev) => ({
        ...prev,
        [BillingAddressFormFields.STATE]: '',
      }));
    if (newState.state) {
      dispatch(
        setBillingAddressError({ [BillingAddressFormFields.STATE]: '' }),
      );
    }
    return setFormData((prevState) => ({ ...prevState, ...newState }));
  };

  /**
   * handleServerErrors
   * @param serverSideErrors
   */
  const handleServerErrors = (serverSideErrors: FormException[]): void => {
    let billingAddressFormErrors: Record<string, string> = {};
    let creditCardFormErrors: Record<string, string> = {};
    serverSideErrors.forEach(
      ({ propertyPath, localizedMessage }: FormException) => {
        if (
          [
            'creditCardNumber',
            'cardVerificationNumber',
            'expirationMonth',
            'expirationYear',
          ].includes(propertyPath) &&
          isNotNullOrUndefinedorEmpty(propertyPath)
        ) {
          if (
            propertyPath === CreditCardFormFields.EXPIRATION_MONTH ||
            propertyPath === CreditCardFormFields.EXPIRATION_YEAR
          ) {
            creditCardFormErrors['expirationError'] = localizedMessage;
          } else {
            creditCardFormErrors[propertyPath] = localizedMessage;
          }
        } else {
          billingAddressFormErrors[propertyPath] = localizedMessage;
        }
      },
    );
    if (isNotEmptyObject(creditCardFormErrors)) {
      dispatch(setCreditCardFormError({ ...creditCardFormErrors }));
    }
    dispatch(setBillingAddressError({ ...billingAddressFormErrors }));
  };

  /**
   * validateField
   * @param name
   * @param value
   * Validates Billing Address Form fields and returns error message (one field at a time)
   */
  const validateField = (name: string, value: string): string => {
    let errorMessage = '';
    switch (name) {
      case BillingAddressFormFields.FIRST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.FIRST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_INVALID;

        break;
      }
      case BillingAddressFormFields.LAST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.LAST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_INVALID;
        break;
      }
      case BillingAddressFormFields.ADDRESS_ONE: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.ADDRESS_REQUIRED;
        break;
      }
      case BillingAddressFormFields.STATE: {
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.STATE_REQUIRED;
        break;
      }
      case BillingAddressFormFields.CITY: {
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.CITY_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.CITY.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.CITY_INVALID;
        break;
      }
      case BillingAddressFormFields.POSTAL_CODE: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_REQUIRED;
        else if (formData[BillingAddressFormFields.COUNTRY] == 'US') {
          if (
            !FIELD_VALIDATION_RULES.US_ZIP_CODES.test(
              getUnformattedNumber(value),
            )
          )
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;
        } else if (formData[BillingAddressFormFields.COUNTRY] == 'CA') {
          if (!FIELD_VALIDATION_RULES.CA_ZIP_CODES.test(value))
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;
        } else if (!FIELD_VALIDATION_RULES.INTERNATIONAL_ZIP_CODES.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;

        break;
      }
      case BillingAddressFormFields.PHONE_NO: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_REQUIRED;
        else if (formData[BillingAddressFormFields.COUNTRY] == 'US') {
          if (
            !FIELD_VALIDATION_RULES.US_CA_PHONE.test(
              getUnformattedNumber(value),
            )
          )
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        } else if (formData[BillingAddressFormFields.COUNTRY] == 'CA') {
          if (!FIELD_VALIDATION_RULES.US_CA_PHONE.test(value))
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        } else if (!FIELD_VALIDATION_RULES.INTERNATIONAL_PHONE.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        break;
      }
      case BillingAddressFormFields.COUNTRY: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.COUNTRY_REQUIRED;
        break;
      }
    }
    return errorMessage;
  };

  const isContinueDisabled = () => {
    const {
      country,
      firstName,
      lastName,
      address1,
      city,
      state,
      phoneNumber,
      postalCode,
    } = formData;
    const hasFormError = formErrors && containsEmptyValues(formErrors);
    const hasEmptyValue = containsSomeEmptyValues({
      country,
      firstName,
      lastName,
      address1,
      city,
      state,
      phoneNumber,
      postalCode,
    });
    return !hasFormError || hasEmptyValue;
  };

  /**
   * validateFields
   * Validates a group of fields
   * Required for validating the entire form at once or when using Loqate Address API
   * @param fields
   * @returns
   */
  const validateFields = (fields: Record<string, any>) => {
    let errorMessages: Record<string, string> = {};
    Object.entries(fields).forEach(([name, value]) => {
      errorMessages[name] = validateField(name, value);
    });
    setFormErrors((prev) => ({ ...prev, ...errorMessages }));
    return containsEmptyValues(errorMessages);
  };

  /**
   *
   * @param event
   * @param fieldType
   */
  const handleFormUpdates = useCallback(
    (fieldType: BillingAddressFormFields, event: ChangeEvent) => {
      let value = (event.target as HTMLInputElement).value;

      switch (fieldType) {
        case BillingAddressFormFields.FIRST_NAME: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.FIRST_NAME, '');
          break;
        }
        case BillingAddressFormFields.LAST_NAME: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.LAST_NAME, '');
          break;
        }
        case BillingAddressFormFields.INSTITUTION: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.INSTITUTION_NAME, '');
          break;
        }
        case BillingAddressFormFields.ATTENTION: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ATTENTION_NAME, '');
          break;
        }
        case BillingAddressFormFields.ADDRESS_ONE: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ADDRESS_ONE, '');
          setLoqateAddressType(''); // Clear Loqate address type when address1 is edited
          break;
        }
        case BillingAddressFormFields.ADDRESS_TWO: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ADDRESS_TWO, '');
          setLoqateAddressType(''); // Clear Loqate address type when address2 is edited
          break;
        }
        case BillingAddressFormFields.CITY: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.CITY, '');
          setLoqateAddressType(''); // Clear Loqate address type when city is edited
          break;
        }
        case BillingAddressFormFields.STATE: {
          setLoqateAddressType(''); // Clear Loqate address type when state is edited
          break;
        }
        case BillingAddressFormFields.POSTAL_CODE: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ZIP_CODE, '');
          setLoqateAddressType(''); // Clear Loqate address type when postalCode is edited
          break;
        }
        case BillingAddressFormFields.PHONE_NO: {
          value = value.replace(/\D/g, '');
          if (value.length > 15) {
            value = value.slice(0, 15);
          }
          if (
            formData[BillingAddressFormFields.COUNTRY] === 'US' ||
            formData[BillingAddressFormFields.COUNTRY] === 'CA'
          ) {
            value = formatPhoneNumber(value);
          }
          break;
        }
        case BillingAddressFormFields.COUNTRY: {
          setLoqateAddressType(''); // Clear Loqate address type when country is edited
          break;
        }
      }

      setFormErrors((prev) => ({
        ...prev,
        [fieldType]: '',
      }));

      dispatch(
        setBillingAddressError({
          [fieldType]: '',
        }),
      );
      updateFormData({ [fieldType]: value });
    },
    [],
  );

  const handleBillingAddSave = async (e: FormEvent) => {
    setLoading(true);
    try {
      e.preventDefault();
      const billingAddReqData = {
        showPhoneMask: true,
        firstName,
        lastName,
        address1,
        address2,
        companyName,
        city,
        state,
        postalCode,
        country,
        attention,
        phoneNumber: getUnformattedNumber(phoneNumber),
        taxExempt: taxExempt,
        sameAsShip: false,
        taxExemptNumber: '',
        taxExemptOrg: '',
        paymentProvider: '',
        callbackFn: '',
        active: Boolean(formData?.active) || false,
        fullName: `${firstName} ${lastName}`,
        fullAddress: `${address1} ${address2 ? `, ${address2}` : ''}`,
        cityStateZip: `${city}, ${state} ${postalCode}`,
        singleLineFullAddress: `${address1} ${address2 ? `, ${address2},` : ''} ${city}, ${state} ${postalCode}`,
        hintInfo: '',
        avsStatus: 'NOT_VALIDATED',
        type: loqateAddressType,
      };
      const reqBody = {
        billingAddress: billingAddReqData,
        method: 'edit',
        paymentMethod: paymentMethod,
        howMany: -1,
        start: 0,
        _dynSessConf: dynSessConf,
        ...(paymentMethod === PAYMENT.ON_ACCOUNT &&
          (selectedPaymentMethodType === PAYMENT.ON_ACCOUNT ||
            selectedPaymentMethodType === PAYMENT.ON_ACCOUNT_CARD) && {
            poNumber,
            ownerId,
            repositoryId,
            saveAsDefault: formData?.active || false,
            accountNumber: '',
            paymentMethod: paymentMethod,
          }),
        ...(ccPaymentMethod === PAYMENT.CREDIT_CARD &&
          (selectedPaymentMethodType === PAYMENT.CREDIT_CARD ||
            selectedPaymentMethodType === PAYMENT.NEW_CREDIT_CARD ||
            selectedPaymentMethodType === PAYMENT.EXISTING_CREDIT_CARD) && {
            cardHolderName,
            cardVerificationNumber,
            creditCardNumber: creditCardNumber.slice(-4),
            expirationMonth,
            expirationYear,
            paymentMethod: ccPaymentMethod,
            saveAsDefault: formData?.active || false,
            ownerId: ccOwnerId,
            repositoryId: ccRepositoryId,
          }),
      };
      const applyBillingRespData = await applyBilling(reqBody, isFurniture);

      if (applyBillingRespData?.status === 'OK') {
        if (
          isNotEmptyObject(applyBillingRespData) &&
          applyBillingRespData?.checkoutResponse
        ) {
          dispatch(
            setLoadCheckoutDetails(applyBillingRespData?.checkoutResponse),
          );
          setIsExpandable && setIsExpandable('none');
          dispatch(setIsSubmitOrder(true));
          setShowPaymentCollapse && setShowPaymentCollapse(false);
        }
      }
      if (applyBillingRespData.formError) {
        triggerCheckoutFormErrorEvent({
          step: 'payment',
          llmAPIVersion,
          isFurniture,
          errors: applyBillingRespData?.formExceptions,
        });
        handleCheckoutServerErrors(
          applyBillingRespData.formExceptions,
          setFormErrors,
        );
        handleServerErrors(applyBillingRespData.formExceptions);
        scrollToFirstError(); // Scroll to the first error field
      }
      return applyBillingRespData;
    } catch (error) {
      console.error('Error in applying billing', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      isNotEmptyObject(billingAddressData?.billingAddressFormData) &&
      !isNewAddress
    ) {
      updateFormData({
        ...formData,
        active: makeDefaultPayment,
      });
    }
  }, []);

  useEffect(() => {
    const isSubmitOrderDisabled = isContinueDisabled() ? false : true;
    if (isSubmitOrderDisabled && isNewAddress && !displaySaveCancelButtons) {
      dispatch(setIsSubmitOrder(isSubmitOrderDisabled));
    }

    dispatch(
      setBillingRequest({
        billingAddressFormData: {
          ...formData,
          companyName: isPOACard
            ? formData.companyName
            : billingAddressData?.billingAddressFormData?.companyName,
          phoneNumber:
            formData.country === 'US' || formData.country === 'CA'
              ? getUnformattedNumber(formData?.phoneNumber)?.slice(0, 10)
              : getUnformattedNumber(formData?.phoneNumber)?.slice(0, 15),
          avsStatus: 'NOT_VALIDATED',
        },
      }),
    );
  }, [formData, billingAddressData?.billingAddressFormData?.companyName]);

  /**
   * handleAutoFillAddress
   * @param address
   */
  const handleAutoFillAddress = (address: LoqateApiAddress) => {
    const newAddressData: Partial<AddressInfo> = {
      [BillingAddressFormFields.COUNTRY]: address.CountryIso2,
      [BillingAddressFormFields.ADDRESS_ONE]: `${address.BuildingNumber} ${address.Street}`,
      [BillingAddressFormFields.ADDRESS_TWO]: address.SubBuilding,
      [BillingAddressFormFields.CITY]: address.City,
      [BillingAddressFormFields.STATE]: address.ProvinceCode,
      [BillingAddressFormFields.POSTAL_CODE]: address.PostalCode,
    };

    // Clear errors for city and postalCode fields
    setFormErrors((prev) => ({
      ...prev,
      [BillingAddressFormFields.CITY]: '',
      [BillingAddressFormFields.POSTAL_CODE]: '',
    }));

    dispatch(
      setBillingAddressError({
        [BillingAddressFormFields.CITY]: '',
        [BillingAddressFormFields.POSTAL_CODE]: '',
      }),
    );

    validateFields(newAddressData);
    updateFormData(newAddressData);
    setLoqateAddressType(address.Type); // Set Loqate address type when auto-selected
  };

  const onBlurHandler = (
    key: string,
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const errorMessage = validateField(key, value);
    if (errorMessage) {
      setFormErrors((prev) => ({ ...prev, [key]: errorMessage }));
      dispatch(
        setBillingAddressError({
          [key]: errorMessage,
        }),
      );
    }
    sendOnBlurDTMEvent({ key, errorMessage, value });
  };

  const handleOnCancel = () => {
    dispatch(
      setBillingRequest({
        billingAddressFormData: {
          ...initialBillingAdd,
          companyName: billingAddressData?.billingAddressFormData?.companyName,
          avsStatus: 'NOT_VALIDATED',
        },
      }),
    );
    onCancel();
  };

  return (
    <>
      <form ref={formRef}>
        <Conditional if={isBillingAdd}>
          <>
            <Conditional if={isNotNullOrUndefinedorEmpty(formHeading)}>
              <div className="d-flex justify-content-between m-0 mb-3">
                <LLHeading tagName="2" className="7">
                  {formHeading}
                </LLHeading>
              </div>
            </Conditional>
            <div className="row mb-ll-3 g-ll-3">
              <div className="col-12">
                <CountryList
                  updateHandler={updateFormData}
                  country={formData[BillingAddressFormFields.COUNTRY]}
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(BillingAddressFormFields.COUNTRY, e)
                  }
                  automationId="co_bill_country"
                />
              </div>
            </div>
            <div className="row mb-ll-3 g-ll-3">
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="First Name"
                  name="firstName"
                  value={formData[BillingAddressFormFields.FIRST_NAME]}
                  errorMsg={formErrors?.[BillingAddressFormFields.FIRST_NAME]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.FIRST_NAME, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.FIRST_NAME, e)
                  }
                  maxLength={35}
                  automationId="co_bill_first_name"
                />
              </div>
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="Last Name"
                  name="lastName"
                  value={formData[BillingAddressFormFields.LAST_NAME]}
                  errorMsg={formErrors?.[BillingAddressFormFields.LAST_NAME]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.LAST_NAME, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.LAST_NAME, e)
                  }
                  maxLength={35}
                  automationId="co_bill_last_name"
                />
              </div>
            </div>
            <div className="row mb-ll-3 g-ll-3">
              <Conditional if={isPOACard}>
                <div className="col-12 col-md-6">
                  <LLInput
                    placeholder="School/Institution (optional)"
                    value={formData[BillingAddressFormFields.INSTITUTION]}
                    onChange={(e) =>
                      handleFormUpdates(BillingAddressFormFields.INSTITUTION, e)
                    }
                    onBlur={(e) =>
                      onBlurHandler(BillingAddressFormFields.INSTITUTION, e)
                    }
                    maxLength={50}
                    automationId="co_bill_company"
                  />
                </div>
              </Conditional>
              <div className={`col-12 ${isPOACard ? 'col-md-6' : ''}`}>
                <LLInput
                  placeholder="Attention (optional)"
                  value={formData[BillingAddressFormFields.ATTENTION]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.ATTENTION, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.ATTENTION, e)
                  }
                  maxLength={80}
                />
              </div>
            </div>
            <div className="row mb-ll-3 g-ll-3">
              <div className="col-12 col-md-6">
                <AddressAutoFill
                  idSuffix="delAddrAutoFill"
                  placeholder="Address 1"
                  name="address1"
                  value={formData[BillingAddressFormFields.ADDRESS_ONE]}
                  errorMsg={formErrors?.[BillingAddressFormFields.ADDRESS_ONE]}
                  country={formData[BillingAddressFormFields.COUNTRY]}
                  onUpdate={(address) => {
                    handleAutoFillAddress(address);
                  }}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.ADDRESS_ONE, e)
                  }
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(BillingAddressFormFields.ADDRESS_ONE, e)
                  }
                  automationId="co_bill_address1"
                />
              </div>
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="Address 2 (optional)"
                  value={formData[BillingAddressFormFields.ADDRESS_TWO]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.ADDRESS_TWO, e)
                  }
                  automationId="co_bill_address2"
                />
              </div>
            </div>
            <div className="row mb-ll-3 g-ll-3">
              <div className="col-12 col-md-4">
                <LLInput
                  placeholder="City"
                  name="city"
                  value={formData[BillingAddressFormFields.CITY]}
                  errorMsg={formErrors?.[BillingAddressFormFields.CITY]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.CITY, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.CITY, e)
                  }
                  maxLength={30}
                  automationId="co_bill_city"
                />
              </div>
              <div className="col-12 col-md-4">
                <StateList
                  updateHandler={updateFormData}
                  country={formData.country}
                  name="state"
                  value={formData[BillingAddressFormFields.STATE]}
                  errorMsg={formErrors?.[BillingAddressFormFields.STATE]}
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(BillingAddressFormFields.STATE, e)
                  }
                  automationId="co_bill_us_state"
                />
              </div>
              <div className="col-12 col-md-4">
                <LLInput
                  placeholder="Zip"
                  name="postalCode"
                  value={formData[BillingAddressFormFields.POSTAL_CODE]}
                  errorMsg={formErrors?.[BillingAddressFormFields.POSTAL_CODE]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.POSTAL_CODE, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.POSTAL_CODE, e)
                  }
                  maxLength={10}
                  automationId="co_bill_postal_code"
                />
              </div>
            </div>
            <div className="row mb-ll-3 g-ll-3 position-relative">
              <div className="col-12">
                <LLInput
                  placeholder="Phone Number"
                  name="phoneNumber"
                  value={
                    formData[BillingAddressFormFields.COUNTRY] === 'US' ||
                    formData[BillingAddressFormFields.COUNTRY] === 'CA'
                      ? formatPhoneNumber(
                          formData[BillingAddressFormFields.PHONE_NO],
                        )
                      : formData[BillingAddressFormFields.PHONE_NO]
                  }
                  errorMsg={formErrors?.[BillingAddressFormFields.PHONE_NO]}
                  onChange={(e) =>
                    handleFormUpdates(BillingAddressFormFields.PHONE_NO, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(BillingAddressFormFields.PHONE_NO, e)
                  }
                  automationId="co_bill_phone"
                />
              </div>
              <div className="position-absolute w-auto input-icon">
                <LLPopover
                  text="Your phone number is required in case we need to contact you about your order."
                  position="top"
                >
                  <span className="ls-icon d-block icn-help-otl"></span>
                </LLPopover>
              </div>
            </div>
          </>
        </Conditional>
        <Conditional if={!isNewAddress}>
          <div
            className={`d-md-flex align-items-center justify-content-between`}
          >
            <div>
              <Conditional if={!makeDefaultPayment}>
                <LLSwitch
                  id={`toggleDefault${id}`}
                  onChange={(e) => {
                    updateFormData({
                      [BillingAddressFormFields.IS_DEFAULT]: (
                        e.target as HTMLInputElement
                      ).checked,
                    });
                  }}
                  value={formData[BillingAddressFormFields.IS_DEFAULT]}
                  checked={formData[BillingAddressFormFields.IS_DEFAULT]}
                >
                  Make Default
                </LLSwitch>
              </Conditional>
            </div>
            <div className="d-flex gap-ll-3 gap-md-ll-10 mt-3 mt-md-0">
              <LLButton
                type="reset"
                variation="tertiary"
                onClick={handleOnCancel}
                customClass={'flex-grow-1'}
              >
                Cancel
              </LLButton>
              <LLButton
                type="submit"
                variation="primary"
                customClass={'flex-grow-1'}
                onClick={(e) => handleBillingAddSave(e)}
              >
                Save
              </LLButton>
            </div>
          </div>
        </Conditional>
      </form>
      {/* Loading Indicator */}
      <Conditional if={loading}>
        <Loader aria-label="Processing your order" />
      </Conditional>
    </>
  );
};

export default BillingAddressForm;
